import type { FC, ReactNode } from 'react'
import type { Session } from '@/types'

import Image from 'next/image'
import Link from 'next/link'

import { cn } from '@/lib/utils'
import { getSpeaker } from '@/constants/metric'

type SessionRowProps = {
    session: Session
    className?: string
}

export const SessionRow: FC<SessionRowProps> = ({ session, className }): ReactNode => {
    const people = session.speakers.map((slug) => getSpeaker(slug)).filter(Boolean)

    return (
        <Link
            href={`/sessions/${session.slug}`}
            className={cn(
                'group flex items-start justify-between gap-5 rounded-[9px] border border-line bg-[#0d0d0d] p-5',
                'transition-colors duration-500 hover:border-[#454545] hover:bg-[#ffffff08]',
                className
            )}
        >
            <div className='flex min-w-0 flex-col gap-3'>
                <div className='flex flex-wrap items-center gap-2'>
                    <span className='font-body text-[12px] font-medium leading-[1.3em] text-ink-mute'>
                        {session.minutes} min
                    </span>

                    <span aria-hidden='true' className='h-[3px] w-[3px] rounded-full bg-[#454545]' />

                    <span className='font-body text-[12px] font-medium leading-[1.3em] text-ink-soft'>
                        {session.kind}
                    </span>

                    <span aria-hidden='true' className='h-[3px] w-[3px] rounded-full bg-[#454545]' />

                    <span className='font-body text-[12px] font-medium leading-[1.3em] text-ink-mute'>
                        {session.time}
                    </span>
                </div>

                <div className='flex flex-col gap-1'>
                    <h4 className='font-sans text-[18px] font-medium leading-[1.2em] text-ink tab:text-[22px]'>
                        {session.title}
                    </h4>

                    <p className='font-body text-[15px] leading-[1.4em] text-ink-soft'>
                        {session.blurb}
                    </p>
                </div>

                <ul className='flex flex-wrap items-center gap-2 pt-1'>
                    {people.map((person) => (
                        <li
                            key={person!.slug}
                            className='flex items-center gap-2 rounded-[9px] border border-line bg-surface py-1 pl-1 pr-2.5'
                        >
                            <span className='relative block h-[20px] w-[20px] shrink-0 overflow-hidden rounded-[9px]'>
                                <Image
                                    src={person!.image}
                                    alt=''
                                    fill
                                    sizes='20px'
                                    className='object-cover'
                                />
                            </span>

                            <span className='font-body text-[12px] font-medium leading-[1.3em] text-ink-soft'>
                                {person!.name}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>

            <div className='relative hidden h-[64px] w-[96px] shrink-0 overflow-hidden rounded-[9px] border border-line sm:block'>
                <Image
                    src={session.image}
                    alt=''
                    fill
                    sizes='96px'
                    className='object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.12]'
                />
            </div>
        </Link>
    )
}
